import React from 'react';

/*
 * Last line of defence for the startup loader. If anything in the loader
 * throws while rendering, this swaps the animation for a plain, static gate
 * (status text and, on a startup failure, Retry) and lets the app in as soon
 * as `ready`. The rest of the tree is never unmounted, so a loader bug can
 * cost the animation but never access to Maintener.
 *
 * Keep the fallback trivially simple: no hooks, no context, no animation.
 */

interface Props {
  statusText: string;
  ready: boolean;
  error?: boolean;
  onRetry?: () => void;
  onDone?: () => void;
  children: React.ReactNode;
}

interface State {
  crashed: boolean;
}

class LoaderBoundary extends React.Component<Props, State> {
  state: State = { crashed: false };
  private doneCalled = false;

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(err: unknown) {
    console.error('Startup loader crashed; continuing without it.', err);
    this.finishIfReady();
  }

  componentDidUpdate() {
    this.finishIfReady();
  }

  private finishIfReady() {
    if (!this.state.crashed || !this.props.ready || this.doneCalled) return;
    this.doneCalled = true;
    this.props.onDone?.();
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    if (this.props.ready) return null;
    const { statusText, error, onRetry } = this.props;
    return (
      <div className="sl-root sl-root--plain">
        <div className="sl-footer">
          <div className="sl-brand" aria-hidden="true">MAINTENER</div>
          <div className="sl-caption" role="status" aria-live="polite">{statusText}</div>
          {error && onRetry && (
            <button type="button" className="sl-retry" onClick={onRetry}>Retry</button>
          )}
        </div>
      </div>
    );
  }
}

export default LoaderBoundary;
